import React, { useState, useRef, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCopy, faLocationCrosshairs, faTimeline, faNoteSticky, faRecycle, faChevronRight, faFileWaveform, faFile, faHistory } from "@fortawesome/free-solid-svg-icons";
import './styles.css';
import Print from "../api/print";
import { GetInvoice } from "../api/getInvoiceAPI";
import { Store, setGridData } from "../utils/store";
import { GETAccessoryList } from "../api/accessoryAPI";

interface MenuContextProps {
    clientX: number;
    clientY: number;
    vinData: string;
    stockData: string;
    vehId: string;
    stockNo: string;
    modelData: string;
    vinNumber: string;
    setError: (error: { code: number; message: string; success: string }) => void;
    setIsOpen: (isOpen: boolean) => void;
    setTitle: (title: string) => void;
}

const MenuContext = ({ clientX, clientY, vinData, stockData, stockNo, modelData, vinNumber, vehId, setError, setIsOpen, setTitle }: MenuContextProps) => {
    const [showFormsSubMenu, setShowFormsSubMenu] = useState<boolean>(false);
    const [menuPosition, setMenuPosition] = useState({top: clientY, left: clientX});
    const [subMenuPosition, setSubMenuPosition] = useState<{top?: number, left?: number}>({});
    const menuRef = useRef<HTMLDivElement>(null);
    const formsSubmenuRef = useRef<HTMLUListElement | null>(null);
    const formsMenuItemRef = useRef<HTMLLIElement | null>(null);
    let Did = sessionStorage.getItem('dealerId');
    const files = sessionStorage.getItem('fileNames');
    const fileNames = files?.split(';').filter(fileName => fileName.trim() !== '');
    let userId: number;

    if (Did) {
        userId = parseInt(Did);
    }

    useEffect(() => {
        if (menuRef.current) {
            const menuRect = menuRef.current.getBoundingClientRect();
            const windowHeight = window.innerHeight;
            const windowWidth = window.innerWidth;

            let newTop = clientY;
            let newLeft = clientX;

            // Check if menu would extend below window
            if (clientY + menuRect.height > windowHeight) {
                newTop = windowHeight - menuRect.height;
            }

            // Check if menu would extend beyond right edge
            if (clientX + menuRect.width > windowWidth) {
                newLeft = windowWidth - menuRect.width;
            }

            // Ensure menu doesn't go above top of window
            if (newTop < 0) {
                newTop = 0;
            }

            // Ensure menu doesn't go beyond left edge
            if (newLeft < 0) {
                newLeft = 0;
            }

            newTop = Math.max(0, Math.min(newTop, windowHeight - menuRect.height));
            newLeft = Math.max(0, Math.min(newLeft, windowWidth - menuRect.width));

            if(newTop !== menuPosition.top || newLeft !== menuPosition.left) {
                setMenuPosition({ top: newTop, left: newLeft });
            }
        }
    }, [clientX, clientY, menuPosition]);

    useEffect(() => {
        if(showFormsSubMenu && formsSubmenuRef.current && formsMenuItemRef.current) {
            const submenuRect = formsSubmenuRef.current.getBoundingClientRect();
            const menuItemRect = formsMenuItemRef.current.getBoundingClientRect();
            const windowHeight = window.innerHeight;
            const windowWidth = window.innerWidth;

            let newSubmenuTop: number | undefined;
            let newSubmenuLeft: number | undefined;

            newSubmenuLeft = menuItemRect.right;
            newSubmenuTop = menuItemRect.top;

            if (newSubmenuLeft + submenuRect.width > windowWidth) {
                newSubmenuLeft = menuItemRect.left - submenuRect.width;
            }

            // If submenu would go beyond bottom window edge, adjust upwards
            if (newSubmenuTop + submenuRect.height > windowHeight) {
                newSubmenuTop = windowHeight - submenuRect.height;
            }

            // Ensure submenu stays within top window bound
            newSubmenuTop = Math.max(0, newSubmenuTop);

            // Update submenu position
            setSubMenuPosition({
                top: newSubmenuTop,
                left: newSubmenuLeft
            });

            if(submenuRect.bottom > windowHeight) {
                const adjustTop = windowHeight - submenuRect.height;
                if(formsSubmenuRef.current) {
                    formsSubmenuRef.current.style.top = `${adjustTop - submenuRect.top}px`;
                }
            }
        }
    }, [showFormsSubMenu]);

    const copyValues = async (values: string) => {
        try {
            await navigator.clipboard.writeText(values);
            setError({ success: "Success", code: 0, message: 'Data has been copied to clipboard' });
        } catch (err) {
            console.log(err)
            setError({ success: "Failure", code: 1, message: 'Failed to copy the Data clipboard' });
        }
    }

    const clearRecords = async (recType: number, data: string) => {
        let res: any;
        let rec: string;
        if (recType === 0) {
            rec = 'clearQR'
        } else {
            rec = 'clearPrint'
        }
        res = await Print(rec, userId, data, '');
        if (res === "Success") {
            setError({ success: "Success", code: 0, message: 'Records have been cleared for vins: ' + data });
            window.location.reload();
        } else {
            setError({ success: "Failure", code: 1, message: 'Error occured while clearing the records of vins' });
        }
    }

    const openModal = (modalType: string) => {
        setIsOpen(true);
        setTitle(modalType);
    }

    const handleFormsHover = () => {
        setShowFormsSubMenu(true);
    }

    const handleFormsLeave = (e: React.MouseEvent<HTMLLIElement, MouseEvent>) => {
        const target = e.relatedTarget as HTMLElement | null;
        if (formsSubmenuRef.current && !formsSubmenuRef.current.contains(target)) {
            setShowFormsSubMenu(false);
        }
    }

    const handleFormsGet = async (vehicleId: string, fileName: string, stock: string, model: string, vinNumber: string) => {
        if (!fileName.includes('Addendum')) {
            sessionStorage.setItem('vinNumber', vehicleId);
            sessionStorage.setItem('printFileName', fileName);
            let res: any
            res = await GetInvoice(vehicleId, userId, fileName);
            if (res) {
                Store.dispatch(setGridData(res));
                openModal('User Form');
            }
        } else {
            let Did = sessionStorage.getItem('dealerId');
            if (Did) {
                sessionStorage.setItem('vinNumber', vinNumber);
                sessionStorage.setItem('stockNumber', stock);
                sessionStorage.setItem('modelNumber', model);
                sessionStorage.setItem('printFileName', fileName);
                let AccessoryData = await GETAccessoryList(parseInt(Did))
                Store.dispatch(setGridData(AccessoryData));
                openModal('Addendum')
            }
        }
    }

    const alterGrid = (clicked: string) => {
        let detailData: any = null;
        if (clicked === "LotHistory") {
            detailData = {lotLocation: 'lotHistory1', vin: vinData}
        } else if (clicked === "vinNotes") {
            detailData = {vinNotes: 'scannedVehicleNotes', vin: vinData}
        } else if (clicked === "scanHistory") {
            let data  = {
                'vin': vinData,
                'stock': stockData
            }
            detailData = {scanHistory: 'scanHistory', data: data}
        }

        if(detailData) {
            const event = new CustomEvent(clicked, { detail: detailData });
            window.dispatchEvent(event);
        }
    }

    return (
        <div ref={menuRef} className="context-menu context-menu-hover"
            style={{
                position: 'fixed', // Change to fixed to ensure precise positioning
                zIndex: 2000, // High z-index to ensure it's on top
                left: `${menuPosition.left}px`, 
                top: `${menuPosition.top}px`,
                display: 'flex',
                flexDirection: 'column'
            }}>
            <ul>
                <li onClick={() => { copyValues(vinData) }}>
                    <div className="p-2 menu-item cursor-pointer flex justify-between items-center">
                        <FontAwesomeIcon icon={faCopy} />
                        <span className="pl-2">Copy Vin</span>
                        <div />
                    </div>
                </li>
                <li onClick={() => { copyValues(stockData) }}>
                    <div className="p-2 menu-item cursor-pointer flex justify-between items-center">
                        <FontAwesomeIcon icon={faCopy} />
                        <span className="pl-2">Copy StockNo</span>
                        <div />
                    </div>
                </li>
                <li onClick={() => { openModal('changeLotLocation') }}>
                    <div className="p-2 menu-item cursor-pointer flex justify-between items-center">
                        <FontAwesomeIcon icon={faLocationCrosshairs} />
                        <span className="pl-2">Change Lot Location</span>
                        <div />
                    </div>
                </li>
                <li onClick={() => { alterGrid('LotHistory') }}>
                    <div className="p-2 menu-item cursor-pointer flex justify-between items-center">
                        <FontAwesomeIcon icon={faTimeline} />
                        <span className="pl-2">Lot History</span>
                        <div />
                    </div>
                </li>
                <li onClick={() => { alterGrid('vinNotes') }}>
                    <div className="p-2 menu-item cursor-pointer flex justify-between items-center">
                        <FontAwesomeIcon icon={faNoteSticky} />
                        <span className="pl-2">Vin Notes</span>
                        <div />
                    </div>
                </li>
                <li onClick={() => { clearRecords(0, vinData) }}>
                    <div className="p-2 menu-item cursor-pointer flex justify-between items-center">
                        <FontAwesomeIcon icon={faRecycle} />
                        <span className="pl-2">Clear QR Scans</span>
                        <div />
                    </div>
                </li>
                <li onClick={() => { clearRecords(1, vinData) }}>
                    <div className="p-2 menu-item cursor-pointer flex justify-between items-center">
                        <FontAwesomeIcon icon={faRecycle} />
                        <span className="pl-2">Clear Print Records</span>
                        <div />
                    </div>
                </li>
                <li onClick={() => { alterGrid('scanHistory') }}>
                    <div className="p-2 menu-item cursor-pointer flex justify-between items-center">
                        <FontAwesomeIcon icon={faHistory} />
                        <span className="pl-2">Scan History</span>
                        <div />
                    </div>
                </li>
                <li onMouseEnter={handleFormsHover} onMouseLeave={handleFormsLeave} className="formsMenu relative" ref={formsMenuItemRef}>
                    <div className="p-2 menu-item cursor-pointer flex justify-between items-center">
                        <FontAwesomeIcon icon={faFileWaveform} />
                        <span className="pl-2">Forms</span>
                        <FontAwesomeIcon icon={faChevronRight} />
                        {showFormsSubMenu && (
                            <ul className="formsSubMenu absolute top-0 w-100" ref={formsSubmenuRef} style={{
                                maxHeight: '200px',
                                overflowY: 'auto',
                                overflowX: 'hidden',
                                position: 'fixed',
                                zIndex: 2001,
                                top: subMenuPosition.top !== undefined 
                                    ? `${subMenuPosition.top}px` 
                                    : '0',
                                left: subMenuPosition.left !== undefined 
                                    ? `${subMenuPosition.left}px` 
                                    : 'full',
                            }}>
                                <div className="context-menu context-menu-hover">
                                    {fileNames?.map((fileName, index) => (
                                        <li key={index} onClick={() => handleFormsGet(vehId, fileName, stockNo, modelData, vinNumber)}>
                                            <div className="p-2 menu-item cursor-pointer flex justify-start items-center w-100">
                                                <FontAwesomeIcon icon={faFile} />
                                                <span className="pl-2">{fileName}</span>
                                            </div>
                                        </li>
                                    ))}
                                </div>
                            </ul>
                        )}
                    </div>
                </li>
            </ul>
        </div>
    )
}

export default MenuContext